import React from 'react';
import './PaymentSuccess.css';
import {Button, Result} from 'antd';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import useLocalization from '../../../Localization';
import { ClockCircleOutlined } from '@ant-design/icons';

const PaymentStatus = () => {
  const navigate = useNavigate();
  const {action, registrationType} = useParams();
  const location = useLocation();
  const {ApplicationString} = useLocalization();

  // Stripe status from redirect state (set when navigating after payment)
  const stripeStatus = location?.state?.status;

  /**
   * Map Stripe → UI Status.
   * When status is undefined (e.g. refresh/direct link), show info not error so we don't wrongly show "Payment Failed".
   */
  const getResultStatus = () => {
    switch (stripeStatus) {
      case 'succeeded':
        return 'success';
      case 'requires_action':
      case 'processing':
        return 'info';
      case 'failed':
      case 'canceled':
        return 'error';
      default:
        return 'success';
    }
  };

  /**
   * Title + Subtitle by Stripe status.
   * undefined = neutral message (e.g. after refresh), not "Payment Failed".
   * Ads/jobs use action-specific copy; events keep registration messaging.
   */
  const getAdsOrJobsContent = () => {
    if (action === 'ads') {
      return {
        title: ApplicationString['paymentSuccess.adsTitle'],
        subTitle: ApplicationString['paymentSuccess.adsSubTitle'],
      };
    }
    if (action === 'jobs') {
      return {
        title: ApplicationString['paymentSuccess.jobsTitle'],
        subTitle: ApplicationString['paymentSuccess.jobsSubTitle'],
      };
    }
    return null;
  };

  const getStatusContent = () => {
    switch (stripeStatus) {
      case 'succeeded': {
        const adsOrJobs = getAdsOrJobsContent();
        if (adsOrJobs) return adsOrJobs;
        return {
          title: ApplicationString['paymentSuccess.registrationCompleted'],
          subTitle: ApplicationString['paymentSuccess.thankYou'],
        };
      }
      case 'requires_action':
      case 'processing':
        return {
          title: 'Payment Processing',
          subTitle:
            'Your registration is received and payment is currently being processed. You will receive confirmation once completed.',
        };
      case 'failed':
      case 'canceled':
        return {
          title: 'Payment Failed',
          subTitle:
            'Unfortunately, your payment could not be completed. Please try registering again.',
        };
      default: {
        const adsOrJobs = getAdsOrJobsContent();
        if (adsOrJobs) return adsOrJobs;
        return {
          title: 'Registration Received',
          subTitle:
            'If you completed payment, check your email for confirmation. You can also go to My Events to see your registrations.',
        };
      }
    }
  };

  const getExtraContent = () => (
    <div
      className='payment-success-extra-content'
      dangerouslySetInnerHTML={{
        __html: ApplicationString['payment-success.registration.extracontent1'],
      }}
    />
  );

  const getButtonConfig = () => {
    switch (action) {
      case 'ads':
        return {
          label: ApplicationString['paymentSuccess.goToAds'],
          path: '/ads',
        };
      case 'jobs':
        return {
          label: ApplicationString['paymentSuccess.goToJobs'],
          path: '/jobs',
        };
      default:
        // Same as Payment Processing UI: bank transfer / async flow uses Stripe `processing`, not only `requires_action`
        if (
          stripeStatus === 'requires_action' ||
          stripeStatus === 'processing'
        ) {
          return {
            label: ApplicationString['paymentSuccess.goToPaymentProcessing'],
            path:
              registrationType === 'sponsorship'
                ? '/myCart/sponsorship'
                : '/myCart/regular',
          };
        }
        return {
          label: ApplicationString['paymentSuccess.goToMyEvents'],
          path:
            registrationType === 'sponsorship'
              ? '/myEvents/sponsorship'
              : '/myEvents/regular',
        };
    }
  };

  const {label, path} = getButtonConfig();
  const {title, subTitle} = getStatusContent();
  const resultStatus = getResultStatus();
  const isProcessing =stripeStatus === 'processing' || stripeStatus === 'requires_action';

  return (
    <div className='payment-success-page'>
      <div className='payment-success-card'>
        <Result
          status={resultStatus}
          icon={
            isProcessing ? (
              <ClockCircleOutlined className='payment-success-processing-clock' />
            ) : ''
          }
          title={title}
          subTitle={
            <div className='payment-success-subtitle'>
              <p>{subTitle}</p>
              <div className='payment-success-refund-note'>
                {getExtraContent()}
              </div>
            </div>
          }
          extra={
            <div className='payment-success-actions'>
              <Button
                type='primary'
                key='home'
                size='large'
                className='payment-success-btn-primary'
                onClick={() => {
                  navigate('/');
                  localStorage.removeItem('attendeeSponsorType');
                }}>
                {ApplicationString['paymentSuccess.backToHome']}
              </Button>
              <Button
                key='events'
                size='large'
                className='payment-success-btn-secondary'
                onClick={() => {
                  navigate(path);
                  localStorage.removeItem('attendeeSponsorType');
                }}>
                {label}
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default PaymentStatus;
